import {
  type APISlideAttributes,
  isVisualizationSlide,
  ViewIds,
} from '@ardoq/api-types';
import type { AppModules } from 'appContainer/types';
import type { SelectedSlideState } from 'streams/selectedSlide/selectedSlide$';
import { slideOperations } from 'streams/slides/slideOperations';
import type { CollectionStream } from 'streams/utils/streamUtils';
import type { SlideOnScreen } from './focusedChromeOperations';

const NO_SLIDE: SlideOnScreen = { kind: 'none' };

/** A slide as it is on screen once opened, which is in its own module. */
export const toOpenedSlide = (slide: APISlideAttributes): SlideOnScreen =>
  isVisualizationSlide(slide)
    ? { kind: 'visualization', viewId: slide.view }
    : { kind: 'other' };

/**
 * The slide on screen, derived from the selected slide.
 *
 * Keyed on `selectedSlideId` rather than `loadedSlideId`, so the answer is
 * settled when the user asks for a slide rather than when its data finishes
 * arriving. A slide `slides$` cannot resolve yet reads as a visualization with
 * `ViewIds.NONE` for that reason: reading it as no slide would let scenario
 * chrome coerce the view the slide is about to show — the bug
 * ardoq-packages#35968 fixed.
 *
 * A slide that is not a visualization only counts while its own module is on
 * screen. Anywhere else it reads as no slide, and the page gets the chrome it
 * would have without one.
 */
export const toSlideOnScreen = (
  { selectedSlideId }: SelectedSlideState,
  { byId }: CollectionStream<APISlideAttributes>,
  selectedModule: AppModules
): SlideOnScreen => {
  if (!selectedSlideId) {
    return NO_SLIDE;
  }
  const slide = byId[selectedSlideId];
  if (!slide) {
    return { kind: 'visualization', viewId: ViewIds.NONE };
  }
  if (
    !isVisualizationSlide(slide) &&
    slideOperations.getSlideTypeModule(slide.type) !== selectedModule
  ) {
    return NO_SLIDE;
  }
  return toOpenedSlide(slide);
};
